/* @flow */
import React, { useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// Components
import { QuickViewButton } from '../QuickViewButton';
import { LazyImage } from '../LazyImage/LazyImage';
import { MoreOptionsBadge } from '../Badges/MoreOptions';
import { SeoLinkComponent } from 'dibs-seo/exports/SeoLink';

// Helpers
import get from 'lodash.get';
import { getSrcSetString } from '../helpers/srcSet';
import { TileContext } from '../helpers/TileContext';

import type { Product_item as ProductItemProp } from './__generated__/Product_item.graphql';

// Styles
import styles from './styles.scss';

type ProductComponentProps = {|
    disableLinks?: boolean,
    pdpUrl: string,
    index: number,
    item: ProductItemProp,
    itemsPerRow: number,
    title: string,
    onClick?: () => void,
    onContentLoaded?: () => void,
    showQuickView?: boolean,
    openQuickView?: () => void,
    showMoreOptionsBadge: boolean,
    showLightBox?: boolean,
    dimensions?: string,
    showQuickViewIcon?: boolean,
    useLoFiLazyLoader: boolean,
    imageLoadVerticalOffset?: number,
    srcSetSizes?: string,
    srcSetWidths?: Array<number>,
    srcSetQuality?: number,
    hideDivider?: boolean,
    hideProductDetails?: boolean,
    hideImagePadding?: boolean,
    imageDraggable?: boolean, // undefined draggable will result in default browser behavior
|};

export const ProductComponent = ({
    disableLinks,
    pdpUrl,
    index,
    item,
    itemsPerRow,
    title,
    onClick,
    onContentLoaded,
    showMoreOptionsBadge,
    showQuickView,
    openQuickView,
    showLightBox,
    dimensions,
    useLoFiLazyLoader,
    showQuickViewIcon,
    imageLoadVerticalOffset,
    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
    hideDivider,
    hideProductDetails,
    hideImagePadding,
    imageDraggable,
}: ProductComponentProps) => {
    const { imageSize } = useContext(TileContext);
    // FINDING-10742: DON'T USE 'firstPhotoWebPath' - product tile is used on
    // trade profiles and dealer storefront
    // By default the smallPath should be used
    const smallPath = get(item, 'productPhotos[0].smallPath');
    const webPath = get(item, 'productPhotos[0].versions[0].webPath') || '';
    const placeholder = get(item, 'productPhotos[0].placeholder');
    const src = smallPath || webPath;
    const masterPath = get(item, 'productPhotos[0].masterOrZoomPath');

    const srcSetAttributes =
        srcSetSizes && masterPath
            ? {
                  srcSet: getSrcSetString(masterPath, {
                      widths: srcSetWidths,
                      quality: srcSetQuality,
                  }),
                  sizes: srcSetSizes,
              }
            : {};
    const thumb = (
        <div
            className={classnames(styles.imageContainer, styles[imageSize])}
            data-tn="product-image-container"
        >
            {index < itemsPerRow ? (
                <img
                    className={styles.image}
                    src={src}
                    alt={title}
                    {...srcSetAttributes}
                    data-tn="product-image"
                    draggable={imageDraggable}
                />
            ) : (
                <React.Fragment>
                    <LazyImage
                        className={styles.image}
                        srcSetAttributes={srcSetAttributes}
                        placeholder={placeholder}
                        alt={title}
                        offsetVertical={imageLoadVerticalOffset}
                        onImageLoad={onContentLoaded}
                        useLoFiLazyLoader={useLoFiLazyLoader}
                        src={src}
                        imageSize={imageSize}
                        imageDraggable={imageDraggable}
                    />
                    <noscript>
                        <img className={styles.image} src={src} alt={title} {...srcSetAttributes} />
                    </noscript>
                </React.Fragment>
            )}
        </div>
    );
    const productClass = classnames(styles.product, {
        [styles.hasLightBox]: showLightBox,
        [styles.hasDivider]: !hideDivider,
    });
    let imageEl = (
        <SeoLinkComponent
            dataTn="product-image-link"
            linkData={{ path: pdpUrl, isLinkable: false }}
            onClick={onClick}
        >
            {thumb}
        </SeoLinkComponent>
    );

    if (disableLinks) {
        imageEl = thumb;
    }
    const imageWrapperClass = classnames(styles.imageWrapper, {
        [styles.hideImageSidePadding]: hideImagePadding,
        [styles.lightBoxImage]: showLightBox,
    });
    return (
        <div className={productClass} draggable="false">
            <div className={imageWrapperClass}>{imageEl}</div>
            {!hideProductDetails && (
                <div
                    className={classnames(styles.productDetailWrapper, {
                        [styles.hideDivider]: hideDivider,
                    })}
                >
                    {showMoreOptionsBadge && <MoreOptionsBadge localizedPdpUrl={pdpUrl} />}
                    {showQuickView && (
                        <QuickViewButton
                            openQuickView={openQuickView}
                            showQuickViewIcon={showQuickViewIcon}
                            hideDivider={hideDivider}
                        />
                    )}
                    {!!dimensions && (
                        <div className={styles.dimensions} data-tn="dimensions">
                            {dimensions}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

ProductComponent.defaultProps = {
    disableLinks: false,
    itemsPerRow: 3,
    useLoFiLazyLoader: false,
    showMoreOptionsBadge: false,
};

export const Product = createFragmentContainer(ProductComponent, {
    item: graphql`
        fragment Product_item on Item
            @argumentDefinitions(photosLimit: { type: "Int", defaultValue: 1 }) {
            productPhotos: photos(limit: $photosLimit) {
                placeholder
                smallPath
                masterOrZoomPath
                versions {
                    webPath
                }
            }
            localizedPdpUrl
        }
    `,
});
